import { computed, onBeforeMount, ref } from "vue"
import emailValidator from "../../foundation/js/inputValidators/emailValidator"
import notEmptyInput from "../../foundation/js/inputValidators/notEmptyInput"
import phoneValidator from "../../foundation/js/inputValidators/phoneValidator"
import isBoolean from "../../foundation/js/inputValidators/isBoolean"
import cprValidator from "../../foundation/js/inputValidators/cprValidator"
import getContactedSubmit from "../../foundation/js/signupFlows/getContactedSubmit"
import newslettersSignupSubmit from "../../foundation/js/signupFlows/newslettersSignupSubmit"
import getGeneralComputedValues from "../../foundation/js/signupFlows/getGeneralComputedValues"
import handleValidateInputAndProceed from "../../foundation/js/signupFlows/handleValidateInputAndProceed"

import SignupStepReceipt from "../../../../Features/Blocks/Newsletters/NewslettersSignup/SignupStepReceipt/SignupStepReceipt.vue"
import ContactStepIntro from "../ContactStepIntro/ContactStepIntro.vue"
import ContactStepContent from "../ContactStepContent/ContactStepContent.vue"
import ContactStepComments from "../ContactStepComments/ContactStepComments.vue"
import ContactStepNewsletters from "../ContactStepNewsletters/ContactStepNewsletters.vue"
import { useRecaptchaToken } from "../../composables/use-recaptcha.composable"

export default {
  name: "ContactCardPermission",
  components: {
    ContactStepIntro,
    ContactStepContent,
    ContactStepComments,
    ContactStepNewsletters,
    SignupStepReceipt,
  },
  props: {
    antiForgeryToken: {
      type: String,
      required: true,
    },
    labels: {
      type: Object,
      required: true,
    },
    formId: {
      type: String,
      required: true,
    },
    formGuid: {
      type: String,
      required: true,
    },
    formIdentifier: {
      type: String,
      required: true,
    },
    formTypeName: {
      type: String,
      required: true,
    },
    apiUrl: {
      type: String,
      required: true,
    },
    newsletterApiUrl: {
      type: String,
      required: true,
    },
    newsletters: {
      type: Array,
      required: false,
      default: () => [],
    },
    newslettersUserInputs: {
      type: Array,
      required: true,
    },
    alignIntroImageRight: {
      type: Boolean,
      required: false,
      default: false,
    },
    skipFirstStep: {
      type: Boolean,
      required: false,
      default: false
    },
    imageSource: {
      type: String,
      required: true,
    },
    imageAltText: {
      type: String,
      required: false,
    },
    backgroundColor: {
      type: String,
      required: false,
      default: null,
    },
    assignTo: {
      type: String,
      required: true,
    },
    enableUserTrack: {
      type: Boolean,
      default: false,
      required: false
      },
      alwaysShowCprField: {
          type: Boolean,
          default: false,
          required: false
      }
  },
  setup(props) {
    const { getRecaptchaToken } = useRecaptchaToken();

    const bgColorClassName = computed(() => {
      return props.backgroundColor ? `nyk-contact-card--${props.backgroundColor}` : ""
    })

    const imagePosClassName = computed(() => {
      return props.alignIntroImageRight ? "nyk-contact-card--image-right" : ""
    })

    const contactCardClassNames = computed(() => {
      return `${bgColorClassName.value} ${imagePosClassName.value}`
    })

    const notRequiredInput = (input) => {
      return true
    }

    const onlyIfCustomer = (input) => {
        if (userInput.value.find((field) => field.field === "isCustomer").value || props.alwaysShowCprField) {
        return cprValidator(input)
      } else {
        return true
      }
    }

    // this object contains the entire set of inputs available for the complete form.
    const userInput = ref([
      ...props.newslettersUserInputs,
      {
        field: "firstname",
        value: "",
        validator: notEmptyInput,
        isValid: false,
      },
      {
        field: "lastname",
        value: "",
        validator: notEmptyInput,
        isValid: false,
      },
      {
        field: "email",
        value: "", // you can prefill the inputfield by adding a value here.
        validator: emailValidator,
        isValid: false,
      },
      {
        field: "phone",
        value: "", // you can prefill the inputfield by adding a value here.
        validator: phoneValidator,
        isValid: false,
      },
      {
        field: "cpr",
        value: "",
        validator: onlyIfCustomer, // should only be validated if the user is a customer
        isValid: false, // is valid as empty string if the user is not a customer
      },
      {
        field: "isCustomer",
        value: null,
        validator: isBoolean,
        isValid: false,
      },
      {
        field: "comment",
        value: "", // you can prefill the inputfield by adding a value here.
        validator: notRequiredInput,
        isValid: false,
      },
    ])

    // this object contains stepinformation.
    // all steps are described here
    let stepsConfigAndOrder = ref([
      {
        step: 0,
        name: "ContactStepIntro",
        completed: props.skipFirstStep,
        isCountableStep: false,
        texts: {
          title: props.labels.title,
          summary: props.labels.introSummary,
          next: props.labels.nextButtonText
        },
      },
      {
        step: 1,
        name: "ContactStepContent",
        completed: false,
        isCountableStep: true,
        texts: {
          title: props.labels.title,
          summary: props.labels.summary,
          next: props.labels.nextButtonText
        }
      },
      {
        step: 2,
        name: "ContactStepComments",
        completed: false,
        isCountableStep: true,
        texts: {
          title: props.labels.title,
          next: props.labels.nextButtonText
        },
      },
      {
        step: 3,
        name: "ContactStepNewsletters",
        completed: false,
        isCountableStep: true,
        texts: {
          title: props.labels.title,
          summary: props.labels.permissionsSummary,
            next: props.labels.submitLabel !== null &&
                props.labels.submitLabel !== undefined &&
                props.labels.submitLabel !== ""
                ? props.labels.submitLabel
                : props.labels.nextButtonText
        },
      },
      {
        step: 4,
        name: "SignupStepReceipt",
        completed: false,
        isCountableStep: false,
        texts: {
          title: props.labels.receiptTitle,
          summary: props.labels.receipt,
        },
      },
    ])

    const displayInputError = ref(false)

    const {
      getNamedFieldInputData,
      resetSignupForm,
      currentStepName,
      currentStepTexts,
      hasPreviousStep,
      stepCounterText,
      showFieldErrorMessage,
      returnToPreviousStep,
    } = getGeneralComputedValues(userInput.value, stepsConfigAndOrder.value)

    const validateInputAndProceed = function (inputData) {
      const validSuccess = handleValidateInputAndProceed(
        inputData,
        userInput.value,
        stepsConfigAndOrder.value,
        currentStepName.value,
        submitData,
        inputData.skipValidation
      )

      displayInputError.value = validSuccess
    }

    const submitData = async function (inputData) {
      const body = {
        formIdentifier: `${props.formTypeName};${props.formIdentifier}`,
        firstName: inputData.find((field) => field.field === "firstname").value,
        lastName: inputData.find((field) => field.field === "lastname").value,
        email: inputData.find((field) => field.field === "email").value,
        phone: inputData.find((field) => field.field === "phone").value,
        comment: inputData.find((field) => field.field === "comment").value,
        isCustomer: inputData.find((field) => field.field === "isCustomer").value,
        cpr: inputData.find((field) => field.field === "cpr").value,
        assignTo: props.assignTo,
        enableUserTrack: props.enableUserTrack === "true",
        formId: props.formId,
        formGuid: props.formGuid
      }

      const formularInformation = {
        formName: props.formTypeName,
        formId: props.formIdentifier
      }

      try {
        await getContactedSubmit(body, props.apiUrl, formularInformation, props.antiForgeryToken, await getRecaptchaToken());
      } catch (error) {
        console.error("Error submitting contact form:", error);
      }

      const newsletters = []

      props.newsletters.filter((newsletter) => {
        const newsletterInput = inputData.find((field) => field.field === newsletter.name)
        if (newsletterInput?.value === true) {
          newsletters.push(newsletter.name)
        }
      })

      if (newsletters.length > 0) {
        const newsletterBody = {
          email: inputData.find((field) => field.field === "email").value,
          firstName: inputData.find((field) => field.field === "firstname").value,
          lastName: inputData.find((field) => field.field === "lastname").value,
          phone: inputData.find((field) => field.field === "phone").value,
          newsletters: newsletters,
          source: window.location.href,
        }

        await newslettersSignupSubmit(newsletterBody, formularInformation, props.newsletterApiUrl, body.isCustomer, props.antiForgeryToken, await getRecaptchaToken())
      }
    }

    return {
      currentStepTexts,
      displayInputError,
      currentStepName,
      getNamedFieldInputData,
      validateInputAndProceed,
      stepCounterText,
      returnToPreviousStep,
      showFieldErrorMessage,
      hasPreviousStep,
      resetSignupForm,
      contactCardClassNames
    }
  },
}
